import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiIconComponent } from '../ni-icon/ni-icon.component';
import { NiToasterComponent } from '../ni-toaster/ni-toaster.component';
import { generateSecureRandomString } from '../../../../shared/utils/random-utils';

interface UploadProgress {
  [key: string]: number;
}

export interface UploadedFile {
  file: File;
  key?: string;
  uploading?: boolean;
  error?: string;
  documentType?: string; // Document type selected for this file
}

@Component({
  selector: 'NiFormFileUpload, ni-file-upload',
  standalone: true,
  imports: [CommonModule, NiIconComponent, NiToasterComponent],
  templateUrl: './ni-file-upload.component.html',
})
export class NiFileUploadComponent implements OnChanges {
  @Input() id: string = `fileupload-${generateSecureRandomString(7)}`;
  @Input() variant: 'default' | 'drag and drop' = 'drag and drop';
  @Input() title: string = '';
  @Input() label!: string;
  @Input() fileTypes!: string;
  @Input() multiSelect = true; // Default to true to allow multiple file selection
  @Input() hint?: string;
  @Input() disabled = false;
  @Input() infoText?: string;
  @Input() maxSize: number = 5; // Max file size in MB
  @Input() uploadedFiles: UploadedFile[] = []; // Files that have been uploaded with their keys
  @Input() required: boolean = false;
  @Input() disableAutoUpload: boolean = false; // Disable automatic upload simulation when parent handles uploads
  @Input() hasError: boolean = false; // Whether to show error state
  @Input() errorMessage: string = ''; // Error message to display
  @Input() hideUploadArea: boolean = false; // When true (e.g. view mode), hide drop zone but show uploaded files

  @Output() onChange = new EventEmitter<File[]>();
  @Output() onRemove = new EventEmitter<number>();
  @Output() onRemoveUploaded = new EventEmitter<string>(); // Emit file key when removing uploaded file
  /** Emitted when View needs the parent to fetch the blob (empty local File). */
  @Output() onDownloadUploaded = new EventEmitter<string>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  files: File[] = [];
  dragActive = false;
  toasterMessage: string = '';
  toasterType: 'error' | 'success' = 'error';
  showToaster: boolean = false;
  imageLoadErrors: Set<string> = new Set(); // Track images that failed to load
  private accessingFileKeys = new Set<string>();

  /** Sync files when uploadedFiles changes (when parent handles uploads) */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uploadedFiles'] && this.disableAutoUpload) {
      // Remove files from pending list if they're now in uploadedFiles
      const uploadedFileNames = new Set(
        this.uploadedFiles.map((uf) => `${uf.file.name}_${uf.file.size}`)
      );

      // Create a new array to avoid "object is not extensible" errors
      this.files = [...this.files].filter((file) => {
        const fileKey = `${file.name}_${file.size}`;
        return !uploadedFileNames.has(fileKey);
      });
    }
  }

  /** Open file dialog manually */
  triggerFileDialog(): void {
    if (!this.disabled) {
      this.fileInput.nativeElement.click();
    }
  }

  /** Handle input change */

  handleChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const selected = Array.from(input.files || []);
  const maxSize = this.getMaxSizeMB();

  const { valid, invalid } = this.filterFilesBySize(selected, maxSize);
  this.showSizeError(invalid, maxSize);

  if (valid.length === 0) {
    input.value = '';
    return;
  }

  const typeValidFiles = this.validateFileTypes(valid);

  if (typeValidFiles.length === 0) {
    input.value = '';
    return;
  }

  this.updateFileList(typeValidFiles);
}


  showToasterMessage(message: string, type: 'success' | 'error') {
    this.toasterMessage = message;
    this.toasterType = type;
    this.showToaster = true;
  }

  closeToaster() {
    this.showToaster = false;
  }

  /** Handle drag events */
  handleDrag(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'dragenter' || event.type === 'dragover') {
      this.dragActive = true;
    } else if (event.type === 'dragleave') {
      this.dragActive = false;
    }
  }

  /** Handle file drop */
handleDrop(event: DragEvent): void {
  event.preventDefault();
  event.stopPropagation();
  this.dragActive = false;

  if (this.disabled) return;

  const selected = Array.from(event.dataTransfer?.files || []);
  const maxSize = this.getMaxSizeMB();

  const { valid, invalid } = this.filterFilesBySize(selected, maxSize);
  this.showSizeError(invalid, maxSize);

  if (valid.length === 0) return;

  const typeValidFiles = this.validateFileTypes(valid);

  if (typeValidFiles.length === 0) return;

  this.updateFileList(typeValidFiles);
}




  private getMaxSizeMB(): number {
    return this.maxSize && Number(this.maxSize) > 0 ? Number(this.maxSize) : 5;
  }

  private filterFilesBySize(files: File[], maxSizeMB: number) {
    const valid: File[] = [];
    const invalid: File[] = [];

    files.forEach(file => {
      const sizeMB = file.size / (1024 * 1024);
      (sizeMB > maxSizeMB ? invalid : valid).push(file);
    });

    return { valid, invalid };
  }

  private showSizeError(files: File[], maxSizeMB: number): void {
    if (files.length === 0) return;

    const fileNames = files.map(f => f.name).join(', ');
    const sizeMB = files[0].size / (1024 * 1024);

    this.showToasterMessage(
      `File size limit exceeded. Maximum allowed size is ${maxSizeMB}MB. ${fileNames} (${sizeMB.toFixed(2)}MB)`,
      'error'
    );
  }

  private validateFileTypes(files: File[]): File[] {
    if (!this.fileTypes || this.fileTypes.trim() === '*' || this.fileTypes.trim() === '') {
      return files;
    }

    const allowed = this.fileTypes
      .split(',')
      .map(t => t.trim().toLowerCase())
      .map(t => (t.startsWith('.') ? t : '.' + t));

    const valid: File[] = [];
    const invalid: File[] = [];

    for (const file of files) {
      const parts = file.name.split('.');
      const ext = parts.length < 2 ? null : '.' + parts.pop()!.toLowerCase();

      (ext && allowed.includes(ext) ? valid : invalid).push(file);
    }

    if (invalid.length > 0) {
      const fileNames = invalid.map(f => f.name).join(', ');
      this.showToasterMessage(`Invalid file type: ${fileNames}`, 'error');
    }

    return valid;
  }

  private updateFileList(validFiles: File[]): void {
    this.files = this.multiSelect
      ? [
          ...this.files,
          ...validFiles.filter(f => {
            const key = this.getFileKey(f);
            return !this.files.some(existing => this.getFileKey(existing) === key);
          })
        ]
      : validFiles.slice(0, 1);

    this.onChange.emit(this.files);
    this.fileInput.nativeElement.value = '';

    if (!this.disableAutoUpload) {
      this.simulateUpload();
    }
  }


  /** Remove selected file */
  handleRemoveFile(index: number): void {
    this.files.splice(index, 1);
    this.onRemove.emit(index);
  }

  /** Remove uploaded file */
  handleRemoveUploadedFile(fileKey: string): void {
    const index = this.uploadedFiles.findIndex((file) => file.key === fileKey);
    if (index > -1) {
      this.uploadedFiles.splice(index, 1);
    }
    // Remove from pending too if orphaned
    this.files = this.files.filter((f) => `${f.name}_${f.size}` !== fileKey);

    this.onRemoveUploaded.emit(fileKey);
  }

  private simulateUpload(): void {
    this.files.forEach((file) => {
      const uniqueKey = `${file.name}_${file.size}`;

      // ❗ Prevent duplicate uploaded entries
      const alreadyExists = this.uploadedFiles.some(
        (uf) => `${uf.file.name}_${uf.file.size}` === uniqueKey
      );
      if (alreadyExists) return;

      setTimeout(() => {
        const uploadedFile: UploadedFile = {
          file,
          key: `uploaded_${Date.now()}_${file.name}`,
          uploading: false,
        };

        // Add only if still not added
        if (!this.uploadedFiles.some((uf) => `${uf.file.name}_${uf.file.size}` === uniqueKey)) {
          this.uploadedFiles.push(uploadedFile);
        }

        // Remove from pending
        this.files = this.files.filter((f) => `${f.name}_${f.size}` !== uniqueKey);
      }, 1500);
    });
  }

  /** Get formatted file types and max size display */
  getFileTypesDisplay(): string {
    if (!this.fileTypes || this.fileTypes.trim() === '' || this.fileTypes.trim() === '*') {
      return `All file types (max. ${this.maxSize})`;
    }
    const extensions = this.fileTypes
      .split(',')
      .map((type) => type.trim().replace('.', '').toUpperCase());
    const extensionsText = extensions.join(', ');
    return `${extensionsText} (max. ${this.maxSize})`;
  }

  /** Generate preview URL for images */
  getFilePreview(file: File): string {
    return URL.createObjectURL(file);
  }

  /** Check if file is an image that should show preview */
  isImageFile(file: File): boolean {
    const imageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    return imageTypes.includes(file.type.toLowerCase()) || 
           /\.(png|jpg|jpeg)$/i.test(file.name);
  }

  /** Get unique key for file to track image load errors */
  getFileErrorKey(file: File): string {
    return `${file.name}_${file.size}_${file.lastModified}`;
  }

  /** Handle image load error */
  onImageError(file: File): void {
    this.imageLoadErrors.add(this.getFileErrorKey(file));
  }

  /** Check if image failed to load */
  hasImageError(file: File): boolean {
    return this.imageLoadErrors.has(this.getFileErrorKey(file));
  }

  /** Get icon name based on file type */
  getFileIcon(file: File): string {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    
    // Check for PDF - use red PDF icon
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return 'pdf-red';
    }
    
    // Check for DOC/DOCX - use blue DOC icon
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        fileType === 'application/msword' ||
        fileName.endsWith('.docx') || 
        fileName.endsWith('.doc')) {
      return 'doc-blue';
    }
    
    // Check for PNG/JPG - use PNG icon
    if (this.isImageFile(file)) {
      return 'png';
    }
    
    // Default to document icon for other file types
    return 'tableFile';
  }

  /** Convert bytes to readable format (e.g. 100 KB, 1.2 MB) */
  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  private getFileKey(file: File): string {
    return `${file.name}_${file.size}_${file.lastModified}`;
  }

  canAccessUploadedFile(uploadedFile: UploadedFile): boolean {
    return !!(uploadedFile?.key && !uploadedFile.uploading && !uploadedFile.error);
  }

  isAccessingUploadedFile(uploadedFile: UploadedFile): boolean {
    const key = uploadedFile?.key;
    return !!key && this.accessingFileKeys.has(key);
  }

  handleViewUploadedFile(uploadedFile: UploadedFile): void {
    if (!this.canAccessUploadedFile(uploadedFile) || !uploadedFile.key) {
      return;
    }
    if (this.accessingFileKeys.has(uploadedFile.key)) {
      return;
    }

    // Prefetched blobs (view/review) are already on the File — open in a new tab.
    if (uploadedFile.file && uploadedFile.file.size > 0) {
      const url = URL.createObjectURL(uploadedFile.file);
      this.openFileInNewTab(url);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }

    // Empty placeholder File — ask parent to fetch by blob key.
    this.accessingFileKeys.add(uploadedFile.key);
    this.onDownloadUploaded.emit(uploadedFile.key);
    // Parent owns the fetch; clear busy flag shortly so a failed fetch can be retried.
    setTimeout(() => {
      if (uploadedFile.key) {
        this.accessingFileKeys.delete(uploadedFile.key);
      }
    }, 8000);
  }

  private openFileInNewTab(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

